import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/api';
import { parseMetarWind, getWindColor, getFlightCategoryColor, getTafFlightCategory, getTafSkyCategory, getTafVisibilityCategory, decodeTafWeather } from '../utils/weatherUtils';

const WelcomeMessage = ({ hoursRefreshKey }) => {
  const { currentUser, settings, currentOrg, isPopupQuestionLoading } = useApp();
  const [metarData, setMetarData] = useState([]);
  const [tafDialog, setTafDialog] = useState({ open: false, loading: false, error: '', data: null });
  const [unreadAnnouncements, setUnreadAnnouncements] = useState([]);
  const [hoursTaughtLast24, setHoursTaughtLast24] = useState(null);
  const isInstructor = Number(currentUser?.user?.roleId) === 3;
  const fetchHoursTaughtLast24 = React.useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/current/lessons/last24/taught`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });

      if (response.ok) {
        const data = await response.json();
        const hours = Number(data?.hoursTaughtLast24);
        setHoursTaughtLast24(Number.isFinite(hours) ? hours : null);
      } else {
        setHoursTaughtLast24(null);
      }
    } catch (error) {
      console.error('Error fetching hours taught in last 24 hours:', error);
      setHoursTaughtLast24(null);
    }
  }, [currentUser?.sessionId]);
  
  useEffect(() => {
    const fetchMetar = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/metar`, {
          headers: {
            'session-id': currentUser?.sessionId
          }
        });
        if (response.ok) {
          const data = await response.json();
          setMetarData(data);
        }
      } catch (error) {
        console.error('Error fetching METAR data:', error);
        setMetarData([]);
      }
    };
    
    const fetchUnreadAnnouncements = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/announcement/list/unread`, {
          headers: {
            'session-id': currentUser?.sessionId
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setUnreadAnnouncements(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Error fetching unread announcements:', error);
      }
    };

    if (currentUser?.sessionId) {
      fetchMetar();
      fetchUnreadAnnouncements();
      if (isInstructor) {
        fetchHoursTaughtLast24();
      } else {
        setHoursTaughtLast24(null);
      }
      const interval = setInterval(() => {
        fetchMetar();
        fetchUnreadAnnouncements();
        if (isInstructor) {
          fetchHoursTaughtLast24();
        }
      }, 300000);
      return () => clearInterval(interval);
    }
  }, [currentUser?.sessionId, isInstructor, fetchHoursTaughtLast24]);

  useEffect(() => {
    if (currentUser?.sessionId && isInstructor && hoursRefreshKey !== undefined) {
      fetchHoursTaughtLast24();
    }
  }, [currentUser?.sessionId, isInstructor, hoursRefreshKey, fetchHoursTaughtLast24]);
  
  const markAnnouncementAsRead = async (announcementId) => {
    try {
      await fetch(`${API_BASE_URL}/announcement/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({ announcementId })
      });
      setUnreadAnnouncements(prev => prev.filter(a => a.id !== announcementId));
    } catch (error) {
      console.error('Error marking announcement as read:', error);
    }
  };

  const fetchTaf = async (metar) => {
    const rawTokens = (metar.rawOb || '').trim().split(/\s+/);
    const rawCodeIndex = ['METAR', 'SPECI'].includes(rawTokens[0]) ? 1 : 0;
    const code = metar.icaoId || metar.stationId || metar.station || metar.airportCode || rawTokens[rawCodeIndex];
    setTafDialog({ open: true, loading: true, error: '', data: null });

    if (!code) {
      setTafDialog({ open: true, loading: false, error: 'No airport code is available for this METAR.', data: null });
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/taf?code=${encodeURIComponent(code)}`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || (response.status === 404 ? 'No current TAF was found within the search radius.' : 'Unable to load TAF data.'));
      }
      setTafDialog({ open: true, loading: false, error: '', data });
    } catch (error) {
      setTafDialog({ open: true, loading: false, error: error.message || 'Unable to load TAF data.', data: null });
    }
  };

  const closeTafDialog = () => setTafDialog({ open: false, loading: false, error: '', data: null });
  const formatTafTime = (value) => {
    if (value == null || value === '') return '';
    const timestamp = typeof value === 'number' ? value * 1000 : value;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
  };
  const getTafAirportName = (data) => {
    const requestedCodes = [data?.requestedAirportCode, data?.requestedAirport?.icaoId, data?.requestedAirport?.iataId, data?.requestedAirport?.faaId]
      .filter(Boolean)
      .map(code => code.toUpperCase());
    const isRequestedAirport = requestedCodes.includes(data?.tafAirportCode?.toUpperCase());
    return data?.tafAirportName
      || data?.tafAirport?.name
      || data?.taf?.name
      || data?.taf?.stationName
      || (isRequestedAirport ? data?.requestedAirport?.name : null);
  };
  const formatRawTaf = (rawTaf) => String(rawTaf || '').replace(/\s+(FM\d{6})\b/g, '\n$1');

  const organizationWebsite = typeof settings?.website === 'string' ? settings.website.trim() : '';
  const organizationWebsiteUrl = organizationWebsite
    ? (/^https?:\/\//i.test(organizationWebsite) ? organizationWebsite : `https://${organizationWebsite.replace(/^\/+/, '')}`)
    : '';
  
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6', minHeight: '52px' }}>
        {(currentOrg?.organizationName || currentOrg?.name) && (
          <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', minWidth: 0, padding: '0 1rem' }}>
            {isPopupQuestionLoading && (
              <div role="status" aria-live="polite" style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: '0.5rem', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
                <span style={{ color: '#526671', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Generating question</span>
                <span aria-hidden="true" style={{ width: '18px', height: '18px', flex: '0 0 18px', border: '2px solid #dce7eb', borderTopColor: '#176b85', borderRadius: '50%', animation: 'popup-question-spin 0.8s linear infinite' }}>
                  <style>{'@keyframes popup-question-spin { to { transform: rotate(360deg); } }'}</style>
                </span>
              </div>
            )}
            {(settings?.logoImage || settings?.logoImageUrl) && (
              organizationWebsiteUrl ? (
                <a href={organizationWebsiteUrl} target="_blank" rel="noopener noreferrer" aria-label={`Visit ${currentOrg.organizationName || currentOrg.name} website`} style={{ display: 'flex', flex: '0 0 52px', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', outlineOffset: '3px' }}>
                  <img src={settings.logoImage || settings.logoImageUrl} alt="" style={{ width: '52px', height: '52px', objectFit: 'contain', filter: 'drop-shadow(0 2px 3px rgba(28, 54, 68, 0.16))' }} />
                </a>
              ) : (
                <img src={settings.logoImage || settings.logoImageUrl} alt="" style={{ width: '52px', height: '52px', flex: '0 0 52px', objectFit: 'contain', filter: 'drop-shadow(0 2px 3px rgba(28, 54, 68, 0.16))' }} />
              )
            )}
            <div style={{ minWidth: 0, maxWidth: 'min(70vw, 720px)', paddingLeft: '1rem', borderLeft: '3px solid #3c8c9b', color: '#203b49', fontSize: '1.4rem', fontWeight: 700, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
              {currentOrg.organizationName || currentOrg.name}
            </div>
          </div>
        )}
        <div style={{ display: 'flex' }}>
          {metarData.length > 0 && (
            <div style={{ flex: 1, padding: '0.5rem', fontSize: '0.7rem' }}>
              {metarData.map((metar, index) => {
                const flightCatColor = getFlightCategoryColor(metar.fltCat);
                const fontWeight = (metar.fltCat === 'IFR' || metar.fltCat === 'LIFR') ? 'bold' : 'normal';
                const text = metar.rawOb.replace(/^METAR\s+/, '');
                const windData = parseMetarWind(metar.rawOb);
                
                // Split text to highlight wind component
                const words = text.split(' ');
                const windIndex = words.findIndex(word => /\d{3}\d{2,3}(G\d{2,3})?KT/.test(word));
                const windColor = windData ? getWindColor(windData, settings?.cautionWind || 15, settings?.maxWind || 25) : 'inherit';
                
                return (
                  <div key={index} style={{ color: flightCatColor, fontWeight }}>
                    <button
                      type="button"
                      title="View TAF forecast"
                      onClick={() => fetchTaf(metar)}
                      style={{ padding: 0, border: 0, background: 'none', color: 'inherit', font: 'inherit', textAlign: 'left', cursor: 'pointer' }}
                    >
                      {words.map((word, wordIndex) => (
                        <span key={wordIndex} style={{ color: wordIndex === windIndex ? windColor : 'inherit' }}>
                          {word}{wordIndex < words.length - 1 ? ' ' : ''}
                        </span>
                      ))}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ padding: '0.5rem', color: '#333', whiteSpace: 'nowrap', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ fontSize: '1.35rem' }}>
              Welcome, {currentUser?.user?.firstName || currentUser?.firstName || currentUser?.first_name}
            </div>
            {isInstructor && (
              <div style={{ fontSize: '0.85rem', marginTop: '0.2rem', color: hoursTaughtLast24 > 6 ? 'orange' : '#333' }}>
                Hours taught in the past 24 hours: {hoursTaughtLast24 !== null ? hoursTaughtLast24 : 0}
              </div>
            )}
          </div>
        </div>
      </div>
      {unreadAnnouncements.length > 0 && (
        <div style={{ borderBottom: '1px solid #dee2e6' }}>
          {unreadAnnouncements.map((announcement, index) => (
            <div key={announcement.id} style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', padding: '0.5rem', backgroundColor: index % 2 === 0 ? '#e3f2fd' : '#bbdefb', borderBottom: index < unreadAnnouncements.length - 1 ? '1px solid #90caf9' : 'none' }}>
              <span style={{ flex: 1 }} dangerouslySetInnerHTML={{ __html: announcement.message || '' }} />
              <button onClick={() => markAnnouncementAsRead(announcement.id)} style={{ marginLeft: '0.5rem', padding: '0.2rem 0.4rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '0.75rem' }}>Dismiss</button>
            </div>
          ))}
        </div>
      )}
      {tafDialog.open && (
        <div
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeTafDialog();
          }}
          style={{ position: 'fixed', inset: 0, zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="taf-dialog-title"
            style={{ position: 'relative', width: 'min(720px, 100%)', maxHeight: '85vh', overflow: 'hidden', padding: '1.5rem', backgroundColor: 'white', borderRadius: '6px', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}
          >
            <button
              type="button"
              aria-label="Close TAF"
              onClick={closeTafDialog}
              style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', border: 'none', background: 'transparent', color: '#475569', fontSize: '1.75rem', lineHeight: 1, cursor: 'pointer' }}
            >
              ×
            </button>
            <h2 id="taf-dialog-title" style={{ margin: '0 2rem 1rem 0', fontSize: '1.25rem' }}>Terminal Aerodrome Forecast</h2>
            {tafDialog.loading && <p role="status">Loading TAF…</p>}
            {tafDialog.error && <p role="alert" style={{ color: '#b91c1c' }}>{tafDialog.error}</p>}
            {tafDialog.data && (
              <div>
                <div style={{ marginBottom: '1rem', color: '#334155' }}>
                  <strong>{tafDialog.data.tafAirportCode || tafDialog.data.requestedAirportCode}</strong>
                  {` · ${getTafAirportName(tafDialog.data) || 'Airport name unavailable'}`}
                  {tafDialog.data.distanceNm != null && ` · ${tafDialog.data.distanceNm} NM from ${tafDialog.data.requestedAirportCode}`}
                </div>
                {tafDialog.data.tafAirportCode && tafDialog.data.requestedAirportCode && tafDialog.data.tafAirportCode !== tafDialog.data.requestedAirportCode && (
                  <p style={{ margin: '0 0 1rem', color: '#7c2d12', fontSize: '0.9rem' }}>
                    This is a nearby airport's forecast, not a forecast for {tafDialog.data.requestedAirportCode}.
                  </p>
                )}
                {tafDialog.data.taf?.rawTAF && (
                  <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', padding: '0.85rem', backgroundColor: '#f1f5f9', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                    {formatRawTaf(tafDialog.data.taf.rawTAF)}
                  </pre>
                )}
                {Array.isArray(tafDialog.data.taf?.fcsts) && tafDialog.data.taf.fcsts.length > 0 && (
                  <div style={{ display: 'grid', gap: '0.5rem', marginTop: '1rem', maxHeight: '38vh', overflowY: 'auto', overscrollBehavior: 'contain', paddingRight: '0.35rem' }}>
                    {tafDialog.data.taf.fcsts.map((forecast, index) => {
                      const decodedWeather = decodeTafWeather(forecast.wxString);
                      const flightCategory = getTafFlightCategory(forecast);
                      const flightCategoryColor = getFlightCategoryColor(flightCategory);
                      const skyCategoryColor = getFlightCategoryColor(getTafSkyCategory(forecast.clouds));
                      const forecastWindValues = [forecast.wspd, forecast.wgst]
                        .filter(value => value != null && value !== '')
                        .map(Number)
                        .filter(Number.isFinite);
                      const forecastWindColor = forecastWindValues.length
                        ? getWindColor(
                          { maxWind: Math.max(...forecastWindValues) },
                          Number(settings?.cautionWind ?? 15),
                          Number(settings?.maxWind ?? 25)
                        )
                        : 'inherit';
                      return (
                      <div key={`${forecast.timeFrom || 'period'}-${index}`} style={{ padding: '0.65rem', borderLeft: `3px solid ${flightCategoryColor}`, backgroundColor: '#f8fafc' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                          <strong>{formatTafTime(forecast.timeFrom)}{forecast.timeTo ? ` – ${formatTafTime(forecast.timeTo)}` : ''}</strong>
                          {flightCategory && <strong style={{ color: flightCategoryColor }}>{flightCategory}</strong>}
                        </div>
                        <div style={{ marginTop: '0.25rem' }}>
                          {decodedWeather && <div style={{ color: flightCategoryColor }}><strong>Weather:</strong> {decodedWeather}</div>}
                          {forecast.visib != null && (
                            <div style={{ color: getFlightCategoryColor(getTafVisibilityCategory(forecast.visib)), fontWeight: 600 }}>
                              Visibility: {forecast.visib} SM
                            </div>
                          )}
                          {(forecast.wdir != null || forecast.wspd != null || forecast.wgst != null) && (
                            <span style={{ color: forecastWindColor, fontWeight: 600 }}>
                              {` · Wind${forecast.wdir != null ? ` ${forecast.wdir}°` : ''}`}
                              {forecast.wspd != null && ` at ${forecast.wspd} kt`}
                              {forecast.wgst != null && ` gusting ${forecast.wgst} kt`}
                            </span>
                          )}
                        </div>
                        {Array.isArray(forecast.clouds) && forecast.clouds.length > 0 && (
                          <div style={{ marginTop: '0.25rem', color: skyCategoryColor }}>
                            <strong>Sky:</strong>{' '}
                            {forecast.clouds.map((cloud, cloudIndex) => (
                              <span key={`${cloud.cover || 'cloud'}-${cloud.base || 'unknown'}-${cloudIndex}`} style={{ fontWeight: 600 }}>
                                {cloudIndex > 0 ? ', ' : ''}{cloud.cover || 'Cloud'}{cloud.base != null ? ` ${cloud.base} ft` : ''}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );})}
                  </div>
                )}
                {!tafDialog.data.taf?.rawTAF && !tafDialog.data.taf?.fcsts?.length && (
                  <p>No readable forecast details were returned.</p>
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
};

export default WelcomeMessage;